import useAuditoriaDashboard from './components/useAuditoriaDashboard';
import AvancoAuditoria from './AvancoAuditoria';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {consultarDashboard, STATUS_PAINEL, dataPainel, quantidadePainel} from './components/dashboardRecepcaoApi';
import './dashboardRecepcao.css';

function useConsulta(acao, id) {
  const [dados, setDados] = useState(null), [erro, setErro] = useState(''), [carregando, setCarregando] = useState(true);
  const sequencia = useRef(0);
  const atualizar = useCallback(async () => {
    const n = ++sequencia.current;
    setCarregando(true);
    try {
      const r = await consultarDashboard(acao, id);
      if (n === sequencia.current) {
        setDados(r);
        setErro('');
      }
      return n === sequencia.current ? r : null;
    } catch (e) {
      if (n === sequencia.current) setErro(e.message);
    } finally {
      if (n === sequencia.current) setCarregando(false);
    }
  }, [acao, id]);

  useEffect(() => {
    const controle = sequencia;
    atualizar();
    const retomar = () => { if (document.visibilityState === 'visible') atualizar(); };
    const timer = setInterval(retomar, 30000);
    window.addEventListener('focus', retomar);
    document.addEventListener('visibilitychange', retomar);
    return () => {
      controle.current++;
      clearInterval(timer);
      window.removeEventListener('focus', retomar);
      document.removeEventListener('visibilitychange', retomar);
    };
  }, [atualizar]);

  return {dados, erro, carregando, atualizar, alterarDados: setDados};
}

function Registro({item, abrir, mostrarData = false, destaque = ''}) {
  return (
    <button type="button" className={'dash-registro' + (destaque ? ' dash-registro-' + destaque : '')} onClick={() => abrir(item.id)} aria-label={'Detalhar agendamento ' + item.id}>
      <span className="dash-registro-hora">{mostrarData ? dataPainel(item.inicio, true) : String(item.inicio).slice(11, 16)}</span>
      <span className="dash-registro-conteudo">
        <strong>{item.cliente}</strong>
        <small>{item.profissional} · OS #{item.id}</small>
      </span>
      <i className="fas fa-chevron-right" aria-hidden="true"/>
    </button>
  );
}

function Modal({titulo, fechar, children, bloqueado = false}) {
  const ref = useRef(null);

  useEffect(() => {
    const anterior = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    ref.current?.querySelector('button')?.focus();

    const key = e => {
      if (e.key === 'Escape') {
        e.preventDefault();
        fechar();
      }
      if (e.key === 'Tab') {
        const els = Array.from(ref.current?.querySelectorAll('button:not([disabled]),a[href],select,textarea,input') || []);
        const first = els[0], last = els[els.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };

    document.addEventListener('keydown', key);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener('keydown', key);
      if (anterior?.isConnected) anterior.focus();
    };
  }, [fechar, titulo]);

  return (
    <div className="dash-modal-fundo" onClick={e => { if (e.target === e.currentTarget) fechar(); }}>
      <section className="dash-modal" role="dialog" aria-modal="true" aria-labelledby="dash-modal-titulo" ref={ref}>
        <header>
          <h2 id="dash-modal-titulo">{titulo}</h2>
          <button className="dash-fechar" type="button" onClick={fechar} disabled={bloqueado}>Fechar</button>
        </header>
        <div className="dash-modal-corpo">{children}</div>
      </section>
    </div>
  );
}

function ListaAgendas({itens, abrir, revisao = false}) {
  const [pagina, setPagina] = useState(0);
  const total = Math.max(1, Math.ceil(itens.length / 10));
  const atual = Math.min(pagina, total - 1);

  if (!itens.length) return <p className="dash-vazio">Nenhum agendamento neste grupo.</p>;

  return (
    <>
      <div className="dash-lista">
        {itens.slice(atual * 10, atual * 10 + 10).map(a => (
          <div className="dash-lista-item" key={a.id}>
            <Registro item={a} abrir={abrir} mostrarData/>
            {revisao
              ? <span className="dash-pendencia">{a.pendencias} {a.pendencias === 1 ? 'item para revisar' : 'itens para revisar'}</span>
              : <span className={'dash-status dash-status-' + a.status}>{STATUS_PAINEL[a.status] || a.status}</span>}
          </div>
        ))}
      </div>
      {total > 1 && (
        <nav className="dash-paginacao" aria-label="Paginação de agendamentos">
          <button type="button" disabled={atual === 0} onClick={() => setPagina(atual - 1)}>Anterior</button>
          <span>{atual + 1} de {total} · {itens.length} registros</span>
          <button type="button" disabled={atual + 1 >= total} onClick={() => setPagina(atual + 1)}>Próxima</button>
        </nav>
      )}
    </>
  );
}

// O lançamento é feito no formulário original da Agenda Geral, nunca pela revisão.
function abrirLancamentoNaAgenda(agenda) {
  const id = Number(agenda?.id);
  if (!Number.isInteger(id) || id <= 0) return;
  const parametros = new URLSearchParams({lancar_insumos_agenda: String(id)});
  const inicio = String(agenda?.inicio || '').slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(inicio)) parametros.set('agenda_inicio', inicio);
  window.location.assign('/agenda-geral?' + parametros.toString());
}

function DetalheAgenda({id, aoJustificar, salvando, definirSalvando}) {
  const [justificativa, setJustificativa] = useState(''), [erroJustificativa, setErroJustificativa] = useState('');
  const {dados, erro, carregando, atualizar} = useConsulta('detalhe', id);
  if (!dados) return <p role={erro ? 'alert' : 'status'}>{erro || 'Carregando agendamento…'}</p>;

  const a = dados.agenda;
  const verificacoes = dados.verificacoes || [];
  const faltas = verificacoes.filter(v => v.situacao !== 'ok');
  const salvar = async e => {
    e.preventDefault();
    if (salvando) return;
    definirSalvando(true); setErroJustificativa('');
    try {
      await consultarDashboard('justificar', id, {assinatura: dados.revisao_assinatura, justificativa: justificativa.trim()});
      await aoJustificar(id);
    } catch (e) { setErroJustificativa(e.message); }
    finally { definirSalvando(false); }
  };

  return (
    <>
      {erro && <p className="dash-erro" role="alert">Falha ao atualizar: {erro}</p>}
      <div className="dash-detalhe-cabecalho">
        <div>
          <h3>{a.cliente}</h3>
          <p>{a.profissional} · {dataPainel(a.inicio, true)} · {STATUS_PAINEL[a.status] || a.status}</p>
        </div>
        <button type="button" className="dash-botao" disabled={carregando} onClick={atualizar}>Atualizar detalhes</button>
      </div>

      <div className="dash-acesso-insumos">
        <div><strong>Lançamento de insumos</strong><small>Abre o formulário original da Agenda Geral, com as regras atuais de prazo e autorização.</small></div>
        <button type="button" className="dash-botao dash-botao-primario" onClick={() => abrirLancamentoNaAgenda(a)} disabled={!a?.alteracao_liberada} title={!a?.alteracao_liberada ? 'Fora da janela de alteração informada pelo servidor' : 'Abrir lançamento na Agenda Geral'}>
          <i className="fas fa-box-open" aria-hidden="true"/> Lançar insumos
        </button>
      </div>
      <h3>Revisão dos insumos</h3>
      {a.status !== 'finalizado'
        ? <p className="dash-vazio">A conferência de referência é feita após a finalização do agendamento.</p>
        : !verificacoes.length
          ? <p className="dash-vazio">Os serviços desta OS não possuem referência inicial cadastrada neste painel.</p>
          : (
            <>
              <p className={faltas.length ? 'dash-aviso' : 'dash-sucesso'}>
                {faltas.length ? faltas.length + ' item(ns) precisam de revisão.' : 'Os requisitos de referência foram atendidos. Esta OS não fica na lista de pendências.'}
              </p>
              <div className="dash-tabela-container">
                <table className="dash-tabela">
                  <thead>
                    <tr><th>Serviço</th><th>Insumo ou grupo</th><th>Referência</th><th>Encontrado</th><th>Conferência</th></tr>
                  </thead>
                  <tbody>
                    {verificacoes.map(v => (
                      <tr key={v.idlanc + '-' + v.grupo} className={v.situacao === 'ok' ? '' : 'dash-linha-revisar'}>
                        <td>{v.servico}<small>Serviço #{v.idservico} · {quantidadePainel(v.quantidade_servico)} lançamento(s)</small></td>
                        <td>{v.insumo}</td>
                        <td>{quantidadePainel(v.minimo)}</td>
                        <td>{quantidadePainel(v.encontrado)}</td>
                        <td>{v.situacao === 'ok' ? 'Atendido' : v.situacao === 'nao_encontrado' ? 'Não encontrado' : 'Abaixo da referência'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {verificacoes.some(v => v.sem_vinculo_utilizado > 0) && <p className="dash-nota">Consumos antigos sem vínculo foram distribuídos entre os serviços, sem contar a mesma quantidade duas vezes.</p>}
            </>
          )}

      <p className="dash-nota">Referências orientativas da rotina do salão. Confira o consumo real; este painel não lança nem altera insumos. Quantidades seguem a unidade utilizada no lançamento.</p>
      {!a.alteracao_liberada && <p className="dash-aviso">A data desta OS está fora da janela de alteração. As regras de bloqueio da agenda continuam valendo.</p>}

      {dados.justificativa_revisao && <div className="dash-sucesso">
        <strong>Revisão justificada</strong><p>{dados.justificativa_revisao.texto}</p>
        <small>{dados.justificativa_revisao.usuario} · {dataPainel(dados.justificativa_revisao.criado_em, true)}</small>
      </div>}
      {faltas.length > 0 && a.alteracao_liberada && !dados.justificativa_revisao && <form className="dash-justificar" onSubmit={salvar}>
        <label htmlFor="dash-justificativa"><strong>Justificar revisão</strong></label>
        <p>Conferiu e o consumo está correto? Informe o motivo para retirar esta OS da lista, sem alterar os insumos.</p>
        <textarea id="dash-justificativa" value={justificativa} onChange={e => setJustificativa(e.target.value)} minLength={10} maxLength={1000} required rows={3} disabled={salvando} placeholder="Ex.: Não havia toalhas disponíveis no salão nesta data."/>
        {erroJustificativa && <p className="dash-erro" role="alert">{erroJustificativa}</p>}
        <button type="submit" className="dash-botao dash-botao-primario" disabled={salvando || carregando || justificativa.trim().length < 10}>{salvando ? 'Gravando…' : 'Gravar justificativa e continuar'}</button>
      </form>}

      <h3>Serviços e produtos lançados</h3>
      <ul className="dash-itens">
        {dados.lancamentos.map(l => <li key={l.id}><span>{l.descricao}<small>{l.profissional || a.profissional} · lançamento #{l.id}</small></span><strong>{quantidadePainel(l.qtd)}</strong></li>)}
        {!dados.lancamentos.length && <li>Nenhum lançamento ativo.</li>}
      </ul>

      <h3>Insumos registrados</h3>
      <ul className="dash-itens">
        {dados.insumos.map(i => <li key={i.id}><span>{i.descricao}<small>Insumo #{i.id}{Number(i.idlanc) > 0 ? ' · serviço lançado #' + i.idlanc : ' · sem vínculo com um serviço específico'}</small></span><strong>{quantidadePainel(i.qtd)}</strong></li>)}
        {!dados.insumos.length && <li>Nenhum insumo ativo encontrado.</li>}
      </ul>

      <div className="dash-modal-rodape">
        <span>Conferência operacional · sem alteração de valores financeiros</span>
        <Link className="dash-botao dash-botao-primario" to={'/agenda-geral?abrir_agendamento=' + encodeURIComponent(String(a.id)) + '&agenda_inicio=' + encodeURIComponent(String(a.inicio || '').slice(0, 10))}>
          <i className="far fa-calendar-check" aria-hidden="true"/> Abrir Agendamento
        </Link>
      </div>
    </>
  );
}

const GRUPOS = [
  {id: 'faltou', titulo: 'Não veio', icone: 'fa-user-times', cor: 'vermelho'},
  {id: 'atrasado', titulo: 'Ainda não chegou', icone: 'fa-clock', cor: 'laranja'},
  {id: 'aguardando', titulo: 'Já aguardando', icone: 'fa-user-clock', cor: 'azul'},
  {id: 'aguardandodmais', titulo: 'Espera elevada', icone: 'fa-hourglass-half', cor: 'vermelhoforte'}
];

export default function DashboardRecepcao() {
  const {dados, erro, carregando, atualizar, alterarDados} = useConsulta('painel');
  const [modal, setModal] = useState(null);
  const [salvandoRevisao, definirSalvando] = useState(false);
  const resumoProfRef=useRef(null);
  const [alturaProfissionais,setAlturaProfissionais]=useState(0);
  const painelPronto=Boolean(dados);
  const auditoriaGrafico=useAuditoriaDashboard(dados?.auditoria_cc,painelPronto);
  useEffect(()=>{
    if(!painelPronto)return;const el=resumoProfRef.current;if(!el)return;
    const medir=()=>setAlturaProfissionais(Math.round(el.getBoundingClientRect().height));medir();
    const observador=typeof ResizeObserver!=='undefined'?new ResizeObserver(medir):null;observador?.observe(el);window.addEventListener('resize',medir);
    return()=>{observador?.disconnect();window.removeEventListener('resize',medir);};
  },[painelPronto]);
  const fechar = useCallback(() => { if (!salvandoRevisao) setModal(null); }, [salvandoRevisao]);
  const selecionar = (id, fila = []) => setModal({tipo: 'agenda', id, fila, titulo: 'Agendamento #' + id + ' — detalhamento operacional'});
  const abrir = id => {
    const fila = (dados?.revisar_insumos || []).map(a => a.id);
    selecionar(id, fila.includes(id) ? fila : []);
  };
  const concluirRevisao = async id => {
    const filaAnterior = modal?.fila || [];
    const restantes = filaAnterior.filter(x => x !== id);
    alterarDados(d => d ? {...d, revisar_insumos: d.revisar_insumos.filter(x => x.id !== id)} : d);
    const novo = await atualizar();
    const fila = novo ? novo.revisar_insumos.map(a => a.id) : restantes;
    const seguinte = filaAnterior.slice(filaAnterior.indexOf(id) + 1).find(x => fila.includes(x)) ?? fila[0];
    if (seguinte !== undefined) selecionar(seguinte, fila);
    else setModal(null);
  };
  const indiceRevisao = modal?.fila?.indexOf(modal.id) ?? -1;
  const listar = (titulo, itens, revisao = false) => setModal({tipo: 'lista', titulo, itens, revisao});

  const d = dados;
  const resumo = d?.resumo || {};
  const agendas = d?.agendas || [];
  // Destaque apenas visual; quem entra/sai da lista continua sendo definido pela API.
  // Data sem fuso explícito é interpretada no horário local do salão.
  const inicioEmMs = item => {
    const texto = String(item.inicio || '').trim().replace(' ', 'T');
    const data = new Date(texto);
    return Number.isFinite(data.getTime()) ? data.getTime() : Number.POSITIVE_INFINITY;
  };
  const agoraMs = Date.now();
  const proximoHorarioMs = Math.min(...((d?.proximos || [])
    .filter(a => a.status === 'inicial')
    .map(inicioEmMs)
    .filter(t => t >= agoraMs)));
  const destaqueProximo = item => {
    if (item.status !== 'inicial') return '';
    const horario = inicioEmMs(item);
    if (horario < agoraMs) return 'atrasado';
    if (horario === proximoHorarioMs) return 'seguinte';
    return '';
  };
  const gruposResumo = [
    ['total', 'Agendamentos do dia', 'fa-calendar-alt', () => agendas],
    ['aguardando', 'Aguardando na recepção', 'fa-user-clock', () => agendas.filter(a => ['aguardando', 'aguardandodmais'].includes(a.status))],
    ['atendimento', 'Em atendimento', 'fa-cut', () => agendas.filter(a => a.status === 'atendimento')],
    ['finalizado', 'Finalizados', 'fa-check-circle', () => agendas.filter(a => a.status === 'finalizado')]
  ];

  return (
    <main className="content-wrapper dash-recepcao dash-recepcao-executiva">
      {erro && <div className="dash-erro" role="alert">{erro}{d && ' · Os dados abaixo são da última atualização concluída.'}</div>}
      {d && (
          <section className="dash-painel dash-painel-alertas">
            <header>
              <div>
                <h2>Fila e alertas da recepção</h2>
                <p>Situações que precisam de atenção operacional</p>
              </div>
              <button type="button" className="dash-botao dash-atualizar" onClick={atualizar} disabled={carregando}><i className={'fas fa-sync-alt ' + (carregando ? 'fa-spin' : '')} aria-hidden="true"/> {carregando ? 'Atualizando…' : 'Atualizar'}</button>
            </header>
            <div className="dash-alertas-grid">
              {GRUPOS.map(g => (
                <article className={'dash-alerta dash-alerta-' + g.cor} key={g.id}>
                  <header>
                    <div className="dash-alerta-titulo">
                      <i className={'fas ' + g.icone} aria-hidden="true"/>
                      <span>{g.titulo}</span>
                    </div>
                    <button type="button" className="dash-alerta-numero" onClick={() => listar(g.titulo, d.alertas[g.id])}>{d.alertas[g.id].length}</button>
                  </header>
                  <div className="dash-alerta-corpo">
                    {d.alertas[g.id].slice(0, 2).map(a => <Registro item={a} abrir={abrir} key={a.id}/>)}
                    {!d.alertas[g.id].length && <p className="dash-vazio">Nenhum agendamento nesta situação.</p>}
                  </div>
                  {d.alertas[g.id].length > 2 && <button className="dash-link" type="button" onClick={() => listar(g.titulo, d.alertas[g.id])}>Ver os {d.alertas[g.id].length} registros</button>}
                </article>
              ))}
            </div>
          </section>

      )}
        <section className="dash-resumos" aria-label="Resumo do dia">
          {gruposResumo.map(([key, label, icon, itens]) => (
            <button type="button" className="dash-resumo" key={key} disabled={!d} onClick={() => listar(label, itens())}>
              <span className="dash-resumo-icon"><i className={'fas ' + icon} aria-hidden="true"/></span>
              <span className="dash-resumo-info"><small>{label}</small><strong>{d ? resumo[key] : '—'}</strong></span>
              <i className="fas fa-angle-right" aria-hidden="true"/>
            </button>
          ))}
          <button type="button" className="dash-resumo dash-resumo-revisao" disabled={!d} onClick={() => listar('Revisão de insumos', d.revisar_insumos, true)}>
            <span className="dash-resumo-icon"><i className="fas fa-clipboard-check" aria-hidden="true"/></span>
            <span className="dash-resumo-info"><small>Revisão de insumos</small><strong>{d ? d.revisar_insumos.length : '—'}</strong></span>
            <i className="fas fa-angle-right" aria-hidden="true"/>
          </button>
        </section>

      {!d && !erro && <p className="dash-carregando" role="status">Carregando o movimento operacional do salão…</p>}

      {d && (
        <div className="dash-layout-analitico">
          <section className="dash-painel dash-painel-profissionais" ref={resumoProfRef}>
            <header>
              <div>
                <h2>Resumo por profissional</h2>
                <p>Equipe ativa que aparece na Agenda Geral</p>
              </div>
              <i className="fas fa-user-friends" aria-hidden="true"/>
            </header>
            <div className="dash-tabela-container dash-scroll-profissionais">
              <table className="dash-tabela dash-por-prof">
                <thead>
                  <tr><th>Profissional</th><th>Total</th><th>Aguardando</th><th>Atendendo</th><th>Finalizados</th></tr>
                </thead>
                <tbody>
                  {d.profissionais.map(p => {
                    const itens = agendas.filter(a => a.profissional_id === p.id);
                    return (
                      <tr key={p.id}>
                        <th>
                          <button type="button" className="dash-link" onClick={() => listar(p.nome + ' — agendamentos do dia', itens)}>{p.nome}</button>
                        </th>
                        {['total', 'aguardando', 'atendimento', 'finalizado'].map(k => (
                          <td key={k}>
                            <button
                              type="button"
                              className={'dash-contagem dash-contagem-' + k}
                              aria-label={(p.resumo[k] || 0) + ' ' + k + ' de ' + p.nome}
                              onClick={() => listar(p.nome + ' · ' + (k === 'total' ? 'Todos' : STATUS_PAINEL[k] || k), itens.filter(a => k === 'total' || (k === 'aguardando' ? ['aguardando', 'aguardandodmais'].includes(a.status) : a.status === k)))}
                            >
                              {p.resumo[k] || 0}
                            </button>
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {!d.profissionais.length && <p className="dash-vazio">Nenhum profissional ativo configurado para exibir na agenda.</p>}
          </section>

          <div className="dash-coluna-direita" style={{'--dash-altura-direita':alturaProfissionais?Math.max(330,alturaProfissionais)+'px':'auto'}}>
          <section className="dash-painel dash-painel-proximos">
            <header>
              <div>
                <h2>Próximos atendimentos</h2>
                <p>Agendados para hoje que ainda não chegaram</p>
              </div>
              <button type="button" className="dash-link" onClick={() => listar('Próximos atendimentos', d.proximos)}>Ver todos ({d.proximos.length})</button>
            </header>
            <div className="dash-proximos dash-scroll-proximos">
              {d.proximos.slice(0, 8).map(a => <Registro key={a.id} item={a} abrir={abrir} destaque={destaqueProximo(a)}/>)}
              {!d.proximos.length && <p className="dash-vazio">Nenhum próximo atendimento pendente de chegada.</p>}
            </div>
          </section>

          <AvancoAuditoria dados={auditoriaGrafico.dados} erro={auditoriaGrafico.erro}/>
          </div>
          <section className="dash-painel dash-painel-insumos">
            <header>
              <div>
                <h2><i className="fas fa-clipboard-check" aria-hidden="true"/> Revisão de insumos</h2>
                <p>OS finalizadas · {dataPainel(d.periodo_insumos.inicio)} a {dataPainel(d.periodo_insumos.fim)} · {d.revisar_insumos.length} para revisar</p>
              </div>
              <span className="dash-referencia">Referências iniciais · 10 serviços</span>
            </header>
            <p className="dash-nota">Itens ausentes ou abaixo da referência. Clique na OS para conferir o serviço, o grupo esperado e o que foi encontrado. A lista se atualiza a cada 30 segundos e ao voltar para esta tela.</p>
            <div className="dash-insumos-area">
              {!d.revisar_insumos.length
                ? <div className="dash-tudo-certo"><i className="far fa-check-circle" aria-hidden="true"/><div><strong>Nenhuma pendência de referência encontrada</strong><p>Nos serviços cobertos pelas regras iniciais, dentro do período informado.</p></div></div>
                : <ListaAgendas itens={d.revisar_insumos} abrir={abrir} revisao/>}
            </div>
          </section>

          <p className="dash-atualizacao">Última atualização: {String(d.gerado_em).slice(11, 19)} · situações conforme registradas na agenda · sem dados financeiros na abertura</p>
        </div>
      )}

      {modal && (
        <Modal titulo={modal.titulo} fechar={fechar} bloqueado={salvandoRevisao}>
          {modal.tipo === 'agenda' && indiceRevisao >= 0 && <nav className="dash-revisao-navegacao" aria-label="Navegação das revisões">
            <button type="button" className="dash-botao" aria-label="Revisão anterior" disabled={salvandoRevisao || indiceRevisao === 0} onClick={() => selecionar(modal.fila[indiceRevisao - 1], modal.fila)}>← Anterior</button>
            <span>Revisão {indiceRevisao + 1} de {modal.fila.length}</span>
            <button type="button" className="dash-botao" aria-label="Próxima revisão" disabled={salvandoRevisao || indiceRevisao === modal.fila.length - 1} onClick={() => selecionar(modal.fila[indiceRevisao + 1], modal.fila)}>Próxima →</button>
          </nav>}
          {modal.tipo === 'agenda'
            ? <DetalheAgenda key={modal.id} id={modal.id} aoJustificar={concluirRevisao} salvando={salvandoRevisao} definirSalvando={definirSalvando}/>
            : <ListaAgendas itens={modal.revisao ? (d?.revisar_insumos || []) : modal.itens} abrir={abrir} revisao={modal.revisao}/>} 
        </Modal>
      )}
    </main>
  );
}
